import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, UploadCloud } from 'lucide-react';
import { isAwaitingShippingFee } from '@mlm/shared';
import { Button, Input, Tabs, TabsContent, TabsList, TabsTrigger } from '@mlm/ui';
import { PaymentInstructions } from '../payment-instructions';
import { useBrand } from '../../storefront/lib/hooks';
import {
  apiErrorMessage,
  ecomPaymentMethodQrUrl,
  getDragonpayChannels,
  getEcomPaymentMethods,
  getOrderGroup,
  initiateGateway,
  performGatewayHandoff,
  uploadManualPayment,
  uploadManualPaymentGroup,
  type EcomPaymentMethodOption,
  type OrderDetail,
} from '../../lib/api';
import { fmtCurrency } from '../../lib/format';

/**
 * The payment surface for an order that is `pending` or `to_pay`, extracted from
 * the order detail page so the same panels can be rendered by the dedicated
 * full-screen payment page as well. Everything here is a verbatim move — the
 * only additions are the `export` keywords and the shared method lookup below,
 * which the three components previously repeated word for word.
 */

/**
 * The registry entry the buyer chose at checkout, resolved from the order's
 * `metadata.payment_method_code`.
 *
 * `payment_method_code` is the stable slug stored at order creation and there is
 * NO route to change it afterwards, which is why a settled-later payment screen
 * can only ever pay the way the order was placed.
 *
 * Returns null when the order carries no code (legacy / public checkout) or when
 * the registry entry has since been renamed, removed or disabled. Callers must
 * treat null as "manual, label unknown" rather than "no payment possible" — the
 * order still has to be payable, and the durable `payment_method_label` snapshot
 * is what keeps the form alive.
 */
function useOrderPaymentMethod(order: OrderDetail): {
  methodCode: string | null;
  method: EcomPaymentMethodOption | null;
} {
  const methodCode =
    ((order.metadata as Record<string, unknown> | null | undefined)?.payment_method_code as
      | string
      | null
      | undefined) ?? null;
  const methodsQ = useQuery({
    queryKey: ['ecom-payment-methods'],
    queryFn: () => getEcomPaymentMethods(),
    enabled: !!methodCode,
  });
  const method = methodCode
    ? (methodsQ.data?.methods.find((m) => m.code === methodCode) ?? null)
    : null;
  return { methodCode, method };
}

/**
 * Online-gateway (Dragonpay / iPay88 / Stripe) payment panel for an awaiting-payment
 * order. The webhook is the source of truth, so this panel polls (via the page
 * query's `refetchInterval`) and just offers a "Pay" / "Pay again" button that
 * re-initiates the gateway handoff. When the buyer returns from the gateway
 * (`?pay=<provider>` in the URL) it shows a "Confirming…" state until the
 * verified webhook flips the order to `paid`.
 */
export function GatewayPaymentPanel({ order }: { order: OrderDetail }) {
  const { method } = useOrderPaymentMethod(order);
  const [params] = useSearchParams();
  const returned = !!params.get('pay'); // browser came back from the gateway
  // The buyer left the gateway's page without paying (Stripe's cancel link).
  const cancelled = params.get('cancelled') === '1';
  // The attempt's own state: a failed / expired one leaves the order payable,
  // so the order status alone would keep this page "confirming" forever.
  const attempt = order.gatewayPayment?.status ?? null;
  const deadAttempt = attempt === 'fail' || attempt === 'void' || attempt === 'unknown';
  const confirming = returned && !cancelled && !deadAttempt;
  const groupId = order.checkoutGroupId ?? null;
  const isDragonpay = method?.gatewayProvider === 'dragonpay';

  // Dragonpay v2 collect REQUIRES a channel (ProcId) — let the buyer pick one
  // from the merchant's enabled list (GCash, cards, banks). iPay88/stub don't.
  const [procId, setProcId] = useState('');
  const channelsQ = useQuery({
    queryKey: ['dragonpay-channels'],
    queryFn: getDragonpayChannels,
    enabled: !!method && method.settlement === 'gateway' && isDragonpay,
    staleTime: 60 * 60 * 1000,
  });

  const mut = useMutation({
    mutationFn: async () => {
      const { handoff } = await initiateGateway({
        ...(groupId ? { checkoutGroupId: groupId } : { orderId: order.id }),
        ...(procId ? { procId } : {}),
      });
      performGatewayHandoff(handoff); // browser leaves the SPA to pay
    },
  });

  if (!method || method.settlement !== 'gateway') return null;

  const needsChannel = isDragonpay; // buyer must pick a channel for Dragonpay
  const canPay = !needsChannel || !!procId;

  return (
    <div data-tour="payment-online" className="rounded-md border p-4 space-y-3">
      {confirming && (
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span>Confirming your payment… this page updates automatically once it clears.</span>
        </div>
      )}
      {!confirming && deadAttempt && (
        <p className="text-sm text-destructive">
          Your last payment didn't go through, and nothing was charged for it. You can try again.
        </p>
      )}
      {!confirming && !deadAttempt && cancelled && (
        <p className="text-sm text-muted-foreground">
          You left the payment page before paying. Pay when you're ready.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        Pay securely with <span className="font-medium text-foreground">{method.label}</span>.
        {needsChannel
          ? ' Choose how you want to pay:'
          : " You'll be taken to the payment page and sent back here when you're done."}
      </p>
      {needsChannel && (
        <select
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={procId}
          onChange={(e) => setProcId(e.target.value)}
          disabled={channelsQ.isLoading}
        >
          <option value="">
            {channelsQ.isLoading ? 'Loading channels…' : 'Select a payment channel…'}
          </option>
          {(channelsQ.data ?? []).map((c) => (
            <option key={c.procId} value={c.procId}>
              {c.name}
            </option>
          ))}
        </select>
      )}
      <Button onClick={() => mut.mutate()} disabled={mut.isPending || !canPay}>
        {mut.isPending ? 'Redirecting…' : returned || deadAttempt ? 'Pay again' : 'Pay now'}
      </Button>
      {mut.isError && (
        <p className="text-xs text-destructive">
          {apiErrorMessage(mut.error) === 'payment_in_progress'
            ? 'Your payment is already going through. This page updates once it clears.'
            : "Couldn't start the payment. Please try again."}
        </p>
      )}
      {channelsQ.isError && needsChannel && (
        <p className="text-xs text-destructive">
          Couldn't load payment channels. Please try again.
        </p>
      )}
    </div>
  );
}

/**
 * Proof-of-payment upload for orders settled via a `settlement='manual'`
 * payment method. We look up the method the buyer chose at checkout from the
 * order's `metadata.payment_method_code`, fetch its `accountLabel`/`instructions`
 * from the registry, and render the form. Wallet-settled orders or orders
 * without a registry code (legacy / public-checkout) just show a generic
 * awaiting-payment notice with no upload.
 */
export function ManualPaymentForm({ order }: { order: OrderDetail }) {
  const brand = useBrand();
  const qc = useQueryClient();
  const { methodCode, method } = useOrderPaymentMethod(order);
  // Durable label fallback: the order stamps `payment_method_label` at creation,
  // so even if the registry entry `methodCode` pointed at was renamed/removed/
  // disabled, we still have a human label to show + submit. This is what keeps
  // the proof form alive — the old gate hid the form whenever `method` was null
  // (the reported "manual pay never asked for a photo" bug).
  const metaLabel = (order.metadata as Record<string, unknown> | null | undefined)
    ?.payment_method_label as string | undefined;
  const methodLabelText =
    method?.label ??
    metaLabel ??
    (typeof methodCode === 'string' ? methodCode : undefined) ??
    'Manual payment';
  const isCod =
    (order.metadata as Record<string, unknown> | null | undefined)?.cod_manual === true ||
    method?.settlement === 'cod_manual';

  // Single-payment: when this order is part of a mixed-cart checkout group, the
  // buyer pays ONCE for every order in the group (one proof, summed total).
  const groupId = order.checkoutGroupId ?? null;
  const groupQ = useQuery({
    queryKey: ['order-group', groupId],
    queryFn: () => getOrderGroup(groupId!),
    enabled: !!groupId,
  });
  const group = groupQ.data ?? null;
  const isGroupPay = !!group && group.orders.length > 1 && !group.allPaid;
  const payAmount = isGroupPay ? group!.payableTotal : order.total;

  const [reference, setReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const mut = useMutation({
    meta: { successMessage: 'Proof of payment submitted.' },
    mutationFn: async () => {
      if (!file) throw new Error('missing_fields');
      // The `currency` column on manual_payments is an ISO 4217 code (the money
      // actually paid). Manual settlement is cash-only (enforced by the admin
      // registry validator), so this is always the tenant's base currency — PHP
      // for the only deployment so far. If multi-region tenants ship later,
      // plumb base_currency through /me or the order.
      if (isGroupPay) {
        return uploadManualPaymentGroup({
          checkoutGroupId: groupId!,
          method: methodLabelText,
          amount: payAmount,
          currency: 'PHP',
          reference: reference || undefined,
          file,
        });
      }
      return uploadManualPayment({
        orderId: order.id,
        method: methodLabelText,
        amount: order.total,
        currency: 'PHP',
        reference: reference || undefined,
        file,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', order.id] });
      if (groupId) qc.invalidateQueries({ queryKey: ['order-group', groupId] });
    },
  });

  // A manual-fee order is unpayable until an admin prices the delivery — show
  // that instead of a proof form the buyer would fill in for the wrong total.
  // Checked BEFORE the gateway fork so a gateway-method manual order says so
  // too rather than rendering nothing.
  if (isAwaitingShippingFee(order)) {
    return (
      <div
        data-tour="payment-finished"
        className="rounded-md border border-warning/30 bg-warning/10 p-3 text-xs"
      >
        <p className="font-medium text-warning">Awaiting shipping fee</p>
        <p className="text-warning/90 mt-1">
          Our team will contact you to confirm the delivery details and set the shipping fee. Once
          it&rsquo;s set, this order becomes payable and the total below will update.
        </p>
      </div>
    );
  }
  // Online-gateway orders are handled by `GatewayPaymentPanel`; yield here.
  if (method?.settlement === 'gateway') return null;
  // Wallet-settled orders already completed at checkout, and COD collects cash
  // on delivery — neither uploads a proof. (Both normally land in a non-pending
  // status, so this branch is defensive.) Show the passive notice.
  if (method?.settlement === 'wallet' || isCod) {
    return (
      <div
        data-tour="payment-finished"
        className="rounded-md border border-warning/30 bg-warning/10 p-3 text-xs"
      >
        <p className="font-medium text-warning">Awaiting payment</p>
        <p className="text-warning/90 mt-1">
          Once admin approves the payment, this order completes and any {brand.kitLabel} codes drop
          into your Code Vault.
        </p>
      </div>
    );
  }
  // Everything else awaiting payment is a manual cash order → render the proof
  // upload form below. This INCLUDES orders whose method can't be resolved from
  // the live registry (code null, or the method was renamed/removed/disabled
  // after the order was placed): the form must NOT disappear, or the buyer can
  // never pay and the admin has nothing to approve (the reported bug).

  // `mut.data` covers the submit that just happened; `order.paymentPending`
  // covers every LATER visit — after a reload, after sign-out/sign-in, and from
  // either /pay/:id or /orders/:id. Without the second half a member who had
  // already sent a proof came back to a blank upload form, which reads as "your
  // payment failed" and invites a duplicate proof (nothing blocks a second
  // pending row). The rejected notice below has always been durable; this is the
  // half that was missing.
  if (mut.data || order.paymentPending) {
    return (
      <div
        data-tour="payment-finished"
        className="rounded-md border border-success/30 bg-success/10 p-3 text-xs"
      >
        <p className="font-medium text-success">Proof submitted</p>
        <p className="text-success/90 mt-1">
          We received your proof of payment. Admin will review it shortly; you'll see the order flip
          to <span className="font-mono">paid</span> once approved.
        </p>
      </div>
    );
  }

  // Item 6: the step-by-step "How to pay" procedure moves to its own panel on
  // the RIGHT (a separate tab on mobile), so the upload form on the left isn't
  // pushed down by a long instructions block. On `lg` the two sit side by side;
  // on narrow screens they become tabs.
  // Admin-uploaded scannable payment QR (e.g. GCash / Maya). Shown prominently
  // above the instructions so the buyer can scan to pay.
  const qrBlock = method?.qrKey ? (
    <div className="rounded-md border p-4 text-center">
      <p className="text-sm font-medium">Scan to pay with {method.label}</p>
      <img
        src={ecomPaymentMethodQrUrl(method.code)}
        alt={`${method.label} payment QR`}
        className="mx-auto mt-2 h-56 w-56 rounded border bg-white object-contain"
      />
      <p className="mt-2 text-xs text-muted-foreground">
        Scan with your payment app, then upload your receipt below.
      </p>
    </div>
  ) : null;

  const procedure = (
    <div className="space-y-3">
      {qrBlock}
      {method?.instructions ? (
        <PaymentInstructions
          methodLabel={method.label}
          markdown={method.instructions}
          accountLabel={method.accountLabel}
        />
      ) : (
        <div className="rounded-md border p-4 text-sm">
          <p className="font-medium">Pay with {methodLabelText}</p>
          {method?.accountLabel && (
            <p className="mt-1 text-muted-foreground">Send to: {method.accountLabel}</p>
          )}
        </div>
      )}
    </div>
  );

  // If a prior proof was rejected, the order was returned to `to_pay` with a
  // history note `payment rejected: <reason>`. Surface the latest one so the
  // member knows why and can re-upload.
  const rejectedNote = [...(order.statusHistory ?? [])]
    .reverse()
    .find((h) => h.status === 'to_pay' && (h.note ?? '').startsWith('payment rejected'));
  const rejectedReason = rejectedNote
    ? rejectedNote.note!.replace(/^payment rejected:\s*/, '').trim()
    : null;

  const uploadForm = (
    <div data-tour="payment-upload" className="rounded-md border p-4 space-y-3">
      {rejectedNote && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-xs">
          <p className="font-medium text-destructive">Previous proof rejected</p>
          <p className="mt-0.5 text-destructive/90">
            {rejectedReason ? `Reason: ${rejectedReason}. ` : ''}
            Please upload a new proof of payment. Use the reference number from your new payment.
          </p>
        </div>
      )}
      {isGroupPay && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 text-xs">
          <p className="font-medium">Single payment for {group!.orders.length} orders</p>
          <p className="mt-0.5 text-muted-foreground">
            This pays for every shop in your purchase at once. Amount payable:{' '}
            <b className="text-foreground">{fmtCurrency(payAmount)}</b>
          </p>
        </div>
      )}
      <div>
        <label className="text-xs text-muted-foreground">Reference / receipt number</label>
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. 1234567890"
        />
      </div>

      <div>
        <label className="text-xs text-muted-foreground">Proof of payment (image or PDF)</label>
        <Input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file && (
          <p className="mt-1 text-xs text-muted-foreground">
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </p>
        )}
      </div>

      {mut.error && <p className="text-xs text-destructive">{prettyUploadError(mut.error)}</p>}

      <Button
        onClick={() => mut.mutate()}
        disabled={!file || mut.isPending}
        size="sm"
        className="w-full"
      >
        <UploadCloud className="h-4 w-4" />
        {mut.isPending ? 'Uploading…' : 'Submit proof of payment'}
      </Button>
    </div>
  );

  return (
    <>
      {/* Narrow screens: tabs so the procedure is a separate tab, not a long
          scroll above the form (no right-margin aside exists on mobile). */}
      <div className="lg:hidden">
        <Tabs defaultValue="pay">
          <TabsList className="w-full">
            <TabsTrigger value="pay" className="flex-1">
              Submit payment
            </TabsTrigger>
            <TabsTrigger value="how" className="flex-1">
              How to pay
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pay" className="mt-3">
            {uploadForm}
          </TabsContent>
          <TabsContent value="how" className="mt-3">
            {procedure}
          </TabsContent>
        </Tabs>
      </div>

      {/* Wide screens: just the form — the step-by-step procedure is rendered in
          the page's right margin by PaymentProcedureAside. */}
      <div className="hidden lg:block">{uploadForm}</div>
    </>
  );
}

/**
 * Desktop-only "How to pay" panel rendered in the order page's right margin so a
 * long step-by-step list has room without crowding the upload form. Resolves the
 * same manual method the form uses (shared query); renders nothing for
 * wallet-settled / code-less orders.
 */
export function PaymentProcedureAside({ order }: { order: OrderDetail }) {
  const { method } = useOrderPaymentMethod(order);
  if (!method || method.settlement !== 'manual') return null;
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Step-by-step
      </p>
      {method.qrKey && (
        <div className="mb-3 rounded-md border p-4 text-center">
          <p className="text-sm font-medium">Scan to pay with {method.label}</p>
          <img
            src={ecomPaymentMethodQrUrl(method.code)}
            alt={`${method.label} payment QR`}
            className="mx-auto mt-2 h-56 w-56 rounded border bg-white object-contain"
          />
        </div>
      )}
      {method.instructions ? (
        <PaymentInstructions
          methodLabel={method.label}
          markdown={method.instructions}
          accountLabel={method.accountLabel}
        />
      ) : (
        <div className="rounded-md border p-4 text-sm">
          <p className="font-medium">Pay with {method.label}</p>
          {method.accountLabel && (
            <p className="mt-1 text-muted-foreground">Send to: {method.accountLabel}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Surface the API's actual rejection reason. The /payments/manual route can
 * fail for several distinct reasons (file too big, malformed currency, order
 * not in a payable status, ClamAV-infected). The generic "Upload failed"
 * message hides the cause; this peels the axios envelope so the user knows
 * what to fix.
 */
function prettyUploadError(err: unknown): string {
  const data = (
    err as {
      response?: {
        data?: { error?: string; issues?: Array<{ message?: string; path?: unknown[] }> };
      };
    }
  ).response?.data;
  const code = data?.error;
  switch (code) {
    case 'order_not_found':
      return 'Order not found.';
    case 'order_not_payable':
      return 'This order is no longer accepting payment.';
    case 'file_too_large':
      return 'File too large. Use an image or PDF under 10 MB.';
    case 'unsupported_file_type':
      return 'Unsupported file type. Upload an image (JPG/PNG) or a PDF.';
    case 'infected_file':
      return 'The file failed the virus scan. Try a different copy of the receipt.';
    case 'duplicate_reference':
      return 'That reference number has already been submitted for this payment method. Double-check the number or try a different one.';
    case 'amount_mismatch':
      return 'The payment amount no longer matches your order total. Refresh the page and try again.';
    case 'group_not_payable':
      return 'These orders are no longer accepting payment.';
    case 'validation_error': {
      const first = data?.issues?.[0]?.message;
      return first ? `Invalid input: ${first}` : 'Invalid input. Check the form and try again.';
    }
    default:
      return 'Upload failed. Check the file (image or PDF under 10 MB) and try again.';
  }
}
